// §4.5 — THE QUOTE BOOK, /sales/quotes (Module 09, slice P3-C-1; saved views
// in PR-E4, D-305).
//
// THE FIRST LIST BORN WITH ALL FOUR WAVE-1 AFFORDANCES (D-241/D-242). The
// customer book was RETROFITTED with two of them (ee47a82 — sort and
// keyboard nav); this screen takes all four at birth, which is the whole
// argument for landing the primitive before the screens that need it:
//
//   1a sticky      — sticky header + frozen quote_no column, CSS-gated off
//                    below 769 px so the phone card contract is untouched.
//   1b sort        — server-side, over the typed `QuotesOrder` enum, URL-
//                    serialized through validateSearch. Carets on exactly four
//                    columns; the contract's own header argues each exclusion.
//   1c selectable  — with the §4.5 bulk bar carrying the two verbs the spec
//                    already declares: Remind and Expire.
//   1d keyboardNav — roving tabindex, Enter opens, Space selects.
//
// THE FOLD IS CACHE-SYNCHRONOUS, and that is a fix rather than a style. The
// landed page-walk idiom keeps an accumulator in state and appends each page
// in a `useEffect`; on a WARM REMOUNT — a manager pressing Back from a quote,
// which is the commonest navigation on this screen — the query cache already
// holds page 1 but the accumulator starts empty, so the first paint is a
// SKELETON over data the browser is already holding, and any walkthrough term
// gated on a row commits to that skeleton. Here `priorRows` holds only the
// pages BEFORE the current cursor and the current page is folded in during
// RENDER, so the first paint of a warm screen has its rows. (The same shape
// also keeps the previous rows on screen while the next page is in flight,
// instead of blanking the table.)
//
// SELECTION IS CLEARED BY THE SAME EFFECT THAT RESETS THE CURSOR STACK — the
// primitive cannot see the query, so it is the screen's contract (wave 1c).
// And the count is of LOADED, SELECTED rows: never "all N matching", which a
// cursor page cannot know and which the bulk verbs could not honour anyway.
// Applying a saved view changes the filters, so it resets both as any filter
// change does (spec 16 §3.12 item 5).
//
// §7 REDACTION IS ABSENCE. `total_minor` is omitted for a reader without
// `sales.read_value`; the cell renders NOTHING — not a dash, which this
// register uses for "there is no such thing". The Total column declares
// `presence` (design-system §3.3): it exists on a page only when a row there
// carries the key, so the column, its caret and its chooser entry all follow
// the payload (D-239 ruling 2, D-301).
//
// THE ADDRESS IS THE WHOLE STATE (PR-E4, D-305 rulings 3 and 4). Every filter
// lives in the URL beside `order` and `view` (screens/search-params.ts), so a
// saved view can store it and a link can carry it, and a default is an absent
// parameter, so a bare `/sales/quotes` sends the request it always sent.
// Every filter and sort write goes through the saved-views controller's
// `write`, which applies the order-token rule while a view is active.
//
// THE CUSTOMER FILTER HAS ONE SOURCE. The 360's "all quotes for this
// customer" link addresses the book as `?customer=<id>`, and the picker now
// writes the same parameter: choosing a customer puts it in the address, and
// typing a new search or pressing the chip's clear takes it out. The screen
// used to keep a `customerTouched` flag that let the picker's choice live in
// component state beside a dead arrival parameter; with one source there is
// nothing for such a flag to arbitrate, and a view that names no customer
// clears a customer the picker chose, as it clears every other filter it does
// not name. The filter is always on the glass: a chip names the customer in
// words with the press that widens the list back out beside it (the picker
// itself only renders once two characters have been typed).
//
// D-301'S MONEY SORT. A money order the role mirror knows this reader is not
// sent totals for is dropped at URL validation, and a reader the mirror
// cannot judge is left to the server, whose 403 naming `sales.read_value` the
// screen answers by dropping the order and asking again. Both arms now hand
// the drop to the controller (`dropRefusedOrder`), which removes `order`
// alone and keeps `view` and every filter, and remembers a refusal of the
// active view's own order, so the view stays applied and unmodified (D-305
// ruling 7).
//
// EXPORT CSV (P3-D PR-D5, D-289 rulings 1, 3, 13). The page head's Export walks
// THIS book's read with the filters and sort on screen and saves every
// matching quote — not the loaded pages, and not the selection
// (components/export-csv.tsx; the columns are lib/exports.ts's). A view's
// hidden columns do not narrow the file (D-305 ruling 23).

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { SalesFilterBar } from "../components/sales-filter-bar";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { bookPager, useBookPages, useBookWindow } from "../hooks/book-pages";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { todayIso } from "../../../../lib/business-dates";
import { useSavedViews, ViewSwitcher, ViewsRegion } from "../../views";
import { quoteFilterLabels } from "../lib/filter-labels";
import { columnPresent, formatMinor } from "../lib/money";
import {
  addressCarriesRefusedValueOrder,
  isValueOrderRefusal,
  isValueOrderToken,
  valueSortVerdict,
} from "../lib/value-order";
import { daysRemaining, quoteStatusVariant, validityBand } from "../lib/quotes";
import { MutationFailureNote } from "../mutation-failure-note";
import { peekCode } from "../../../../components/peek-address";
import { SalesPeek, useSalesPeek } from "../components/peek/sales-peek";
import { quotesCsv, quotesFilters } from "../lib/exports";
import {
  SalesExportButton,
  SalesExportNotes,
  SalesExportSlot,
  useSalesExport,
} from "../components/export-csv";
import { isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import { useCustomer360, useCustomers, useOwnerIndex } from "../hooks/sales-queries";
import { useReadsHeldOffline } from "../lib/reads";
import {
  fetchQuotesPage,
  useExpireQuotes,
  useQuotes,
  useRemindQuotes,
  type Quote,
  type QuotesQuery,
} from "../hooks/quote-queries";
import {
  compactSearch,
  isQuotesOrderToken,
  QUOTE_ORIGINS,
  QUOTE_STATUSES,
  QUOTES_DEFAULT_ORDER,
  validateSalesQuotesSearch,
  type QuotesOrderToken,
  type SalesQuotesSearch,
} from "./search-params";

/** The wire enum's ASC base keys — the type a column's `sortKey` may carry
 * (D-142: derived from the generated client, never a free string). */
type QuotesOrderKey = QuotesOrderToken extends `-${infer Base}` ? Base : QuotesOrderToken;

const STATUSES = QUOTE_STATUSES;

const ORIGINS = QUOTE_ORIGINS;

/** The outcome of one bulk press, per row — so a partially refused fan-out
 * reports what actually happened instead of one thrown error losing the rest. */
interface BulkOutcome {
  readonly done: number;
  readonly failed: number;
}

/** WHICH bulk verb ran. It has to be carried with the failure, because the two
 * verbs share one error banner and the banner used to be hard-wired to the
 * reminder string: a failed Expire reported "Could not send every reminder."
 * and the sibling key `bulkExpireFailed` was never rendered by anything. A
 * manager told the wrong verb failed goes looking in the wrong place. */
type BulkVerb = "remind" | "expire";

export function SalesQuotesScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  // The side peek (spec 09 §4.15, D-314): a row's Peek button opens the
  // record beside the book, and a row click still opens its page.
  const peek = useSalesPeek();

  const [customerText, setCustomerText] = useState("");
  const customerQ = useDebouncedValue(customerText.trim(), 250);

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkOutcome, setBulkOutcome] = useState<BulkOutcome | undefined>(undefined);
  const [bulkError, setBulkError] = useState<unknown>(undefined);
  const [bulkVerb, setBulkVerb] = useState<BulkVerb>("remind");
  const [bulkBusy, setBulkBusy] = useState(false);

  const owners = useOwnerIndex(perms.isAdmin);
  // A member directory the browser holds offline stays pending for the whole
  // cut, so the owner reads as its short id until it answers, the way the
  // 360 and the peek read it (#293).
  const directoryOffline = useReadsHeldOffline();
  const ownersResolving = owners.isPending && !directoryOffline;
  // The customer filter is a SEARCH, not a dropdown of every customer: a
  // tenant with 800 customers cannot be offered as a Select, and offering the
  // first page of them would silently filter the filter.
  const customerMatches = useCustomers({ q: customerQ }, undefined, customerQ.length > 1);

  // THE ADDRESS. D-301: validated with the reader's verdict, so a money sort
  // the mirror KNOWS this reader is not sent totals for leaves the address
  // before any request is built (lib/value-order.ts). The same validator
  // judges a saved view's stored order when a view is applied.
  const rawSearch: Record<string, unknown> = useSearch({ strict: false });
  const valueSort = valueSortVerdict(perms);
  const validate = useMemo(
    () => (raw: Record<string, unknown>) => validateSalesQuotesSearch(raw, valueSort),
    [valueSort],
  );
  const search = useMemo(() => validate(rawSearch), [rawSearch, validate]);
  const setSearch = (next: SalesQuotesSearch) => {
    void navigate({ to: "/sales/quotes", search: compactSearch(next), replace: true });
  };
  const order = search.order ?? QUOTES_DEFAULT_ORDER;
  const customerId = search.customer;

  /** The name behind `?customer=<id>` — resolved through the 360's OWN read,
   * never from the loaded rows. A row's `customer_display_name` would be
   * cheaper, but `priorRows` survives one render past a filter change (the
   * fold runs before the effect that clears it), so the chip could name the
   * customer the reader just left. This read is keyed by the id itself and
   * cannot: it is the same query key the customer 360 filled a moment ago, so
   * following the deep link costs no request at all, and it fires only while a
   * customer filter is actually in force. */
  const filteredCustomer = useCustomer360(customerId ?? "");
  const filteredCustomerName = filteredCustomer.data?.customer.display_name;

  const query: QuotesQuery = useMemo(
    () => ({
      ...(search.status !== undefined ? { status: search.status } : {}),
      ...(search.origin !== undefined ? { origin: search.origin } : {}),
      ...(search.owner !== undefined ? { owner_user_id: search.owner } : {}),
      ...(search.customer !== undefined ? { customer_id: search.customer } : {}),
      // Two-value string enum on the wire, never a coerced boolean (the
      // module-05 mistake the contract itself restates as `BoolQuery`).
      ...(search.off_list === true ? { off_list: "true" as const } : {}),
      ...(search.created_from !== undefined ? { created_from: search.created_from } : {}),
      ...(search.created_to !== undefined ? { created_to: search.created_to } : {}),
      ...(search.order !== undefined ? { order: search.order } : {}),
    }),
    [search],
  );

  // Keyed on WHAT the query asks, not on the memo's identity.
  const queryKey = JSON.stringify(query);

  /** Is a FILTER narrowing this book — not a sort? The empty state below used
   * to ask `queryKey === "{}"`, which counts the URL's `order` as a filter: a
   * reader who sorted an EMPTY book by quote number was told to widen filters
   * they had never set, instead of being invited to write the first quote.
   * The order list states the same rule beside its own two empties. */
  const isFiltered =
    search.status !== undefined ||
    search.origin !== undefined ||
    search.owner !== undefined ||
    search.customer !== undefined ||
    search.off_list !== undefined ||
    search.created_from !== undefined ||
    search.created_to !== undefined;
  useEffect(() => {
    // Wave 1c's screen-side contract: a changed filter or sort invalidates the
    // selection, because the rows it referred to are no longer the rows on
    // screen. Clearing it here — in the same effect that resets the cursor
    // stack — is what makes that impossible to forget.
    setSelected(new Set());
    setBulkOutcome(undefined);
  }, [queryKey]);

  const refused = isSalesReadRefused(perms, perms.canOpenQuotes && perms.has("sales.read"));
  // One page at a time (D-560): the stack starts again at page 1 whenever
  // the query does.
  const pages = useBookPages(queryKey);
  const cursor = pages.cursor;
  // D-301: an address carrying a money order waits for the role feed rather
  // than sending a request the validator may be about to drop.
  const holdForFeed =
    search.order !== undefined && isValueOrderToken(search.order) && valueSort === "pending";

  // THE ROWS THE CONTROLLER JUDGES BY. The saved-views controller needs the
  // loaded rows (the chooser, the Total column's `presence`, the fence that
  // reads it, Modified) AND gates this very read (`listEnabled`), so the rows
  // are read from the list's cache entry through a passive observer of the
  // same key BEFORE the controller runs, and the live read that fetches comes
  // after it. Both observe one query, so within a render they hold the same
  // page. THE CACHE-SYNCHRONOUS FOLD (see the header): the current page is
  // folded in during render, so a warm remount paints rows on its FIRST pass.
  //
  // THE REVEAL GATE (R11, PR-E4 wave 4): the controller's verdict, carried in
  // state and starting closed, as on the work-order list. While it is closed
  // the observer's page, the live page and the settle read as absent, since on
  // `?view=<id>` alone the list key is the bare list's until the view applies,
  // and a bare page already in the cache would be revealed and then replaced.
  const [listGate, setListGate] = useState(false);
  const shown = useQuotes(query, cursor, false);
  const shownData = listGate ? shown.data : undefined;
  const rows = useMemo(() => shownData?.items ?? [], [shownData]);
  const turning = listGate && shown.isPlaceholderData;
  const pageView = useBookWindow(pages, {
    items: rows,
    nextCursor: shownData?.next_cursor,
    turning,
    settled: shownData !== undefined && !shown.isFetching,
  });

  const today = todayIso();
  const canSend = perms.has("sales.quote.send");
  const remind = useRemindQuotes();
  const expire = useExpireQuotes();

  const columns: DataTableColumn<Quote, QuotesOrderKey>[] = [
    {
      key: "quote_no",
      kind: "id",
      header: t("sales.quotes.colNo"),
      id: true,
      sortKey: "quote_no",
    },
    {
      key: "customer",
      phonePriority: 1,
      header: t("sales.quotes.colCustomer"),
      // No caret: a cross-table display name is not honest-cheap to sort
      // (D-242's rule), and the contract omits the token for the same reason.
      render: (row) => row.customer_display_name,
    },
    {
      // D-239 ruling 2 + D-301, as the column's `presence` (design-system
      // §3.3, D-305 ruling 8): the Total column, and with it the money sort's
      // caret and its chooser entry, exists only on a page whose rows carry
      // the key. The PAYLOAD decides, not the role mirror: `total_minor` is
      // non-nullable, so every row sent to a holder of `sales.read_value`
      // carries it and no row sent to anyone else does. A custom role the
      // mirror cannot see into keeps the caret its rows prove. `sortKey` is
      // how the saved-views controller finds this column for a stored
      // `±total_minor` (spec 16 §3.12 item 7's presence arm).
      key: "total_minor",
      kind: "money",
      phonePriority: 2,
      header: t("sales.quotes.colTotal"),
      numeric: true,
      sortKey: "total_minor",
      sortFirst: "desc",
      presence: (loaded) => columnPresent(loaded, (row) => row.total_minor),
      // §7 OMISSION: absent key → NOTHING. Never a dash.
      render: (row) =>
        row.total_minor === undefined ? null : (
          <span className="num">{formatMinor(row.total_minor, row.currency, i18n.language)}</span>
        ),
    },
    {
      key: "status",
      kind: "status",
      header: t("sales.quotes.colStatus"),
      render: (row) => (
        <StatusChip
          variant={quoteStatusVariant(row.status)}
          data-testid="sales-quote-status"
          data-status={row.status}
        >
          {t(`sales.quoteStatus.${row.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "viewed",
      header: t("sales.quotes.colViewed"),
      // D-241 viewed-tracking. The chip exists only once the quote has been
      // SENT: before that there is no link to open, and "Not opened" on a
      // draft would be a fact about nothing. `sent_revision_no` is the honest
      // test — it survives the quote going back to draft for a revision.
      render: (row) => {
        if (row.first_viewed_at !== null) {
          return (
            <StatusChip variant="accent" data-testid="sales-quote-viewed" data-viewed="true">
              {t("sales.quotes.viewedYes")}
            </StatusChip>
          );
        }
        if (row.sent_revision_no === null) return null;
        return (
          <StatusChip
            variant="neutral"
            dot={false}
            data-testid="sales-quote-viewed"
            data-viewed="false"
          >
            {t("sales.quotes.viewedNo")}
          </StatusChip>
        );
      },
    },
    {
      key: "valid_until",
      kind: "date",
      phonePriority: 3,
      header: t("sales.quotes.colValid"),
      // First activation ASCENDING: the actionable read of a validity column
      // is "what dies soonest", which is the triage this screen exists for.
      sortKey: "valid_until",
      render: (row) => {
        const band = validityBand(row.valid_until, today);
        if (band === "absent") return null;
        const left = daysRemaining(row.valid_until, today);
        return (
          <span data-testid="sales-quote-validity" data-band={band}>
            <span className={band === "urgent" || band === "expired" ? "sales-valid-urgent" : ""}>
              {formatBusinessDate(row.valid_until ?? "", i18n.language)}
            </span>
            {band === "urgent" && left !== undefined ? (
              <span className="sales-provisional">
                {t("sales.quotes.validDays", { count: left })}
              </span>
            ) : null}
            {band === "expired" ? (
              <span className="sales-provisional">{t("sales.quotes.validExpired")}</span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: "origin",
      header: t("sales.quotes.colOrigin"),
      render: (row) => (
        <StatusChip
          variant={row.origin === "agent" ? "accent" : "neutral"}
          dot={row.origin === "agent"}
          data-testid="sales-quote-origin"
          data-origin={row.origin}
        >
          {t(`sales.quotes.origin.${row.origin}`)}
        </StatusChip>
      ),
    },
    {
      key: "revision_count",
      header: t("sales.quotes.colRevisions"),
      numeric: true,
      render: (row) => (
        <span className="num">{t("sales.quotes.revision", { count: row.revision_count })}</span>
      ),
    },
    {
      // The default sort's column — it carries the descending aria-sort on a
      // first paint with a bare URL, and is the way back to newest-first.
      key: "created_at",
      kind: "date",
      header: t("sales.quotes.colCreated"),
      age: true,
      sortKey: "created_at",
      sortFirst: "desc",
      render: (row) => formatTimestamp(row.created_at, i18n.language),
    },
  ];

  const views = useSavedViews<SalesQuotesSearch, Quote, QuotesOrderKey>({
    screenKey: "sales.quotes",
    search,
    setSearch,
    validate,
    columns,
    rows,
    rowsLoading: !listGate || shown.isFetching,
    // A refused stored filter is named in this bar's own words (V5).
    filterLabels: quoteFilterLabels(t),
  });
  if (listGate !== views.listEnabled) setListGate(views.listEnabled);

  const live = useQuotes(query, cursor, !refused && !holdForFeed && listGate);
  // The live read as the screen sees it: nothing while the gate is closed.
  const page = {
    data: listGate ? live.data : undefined,
    error: listGate ? live.error : null,
    isPending: !listGate || live.isPending,
    isFetching: listGate && live.isFetching,
    fetchStatus: listGate ? live.fetchStatus : "idle",
    refetch: live.refetch,
  };

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  // D-301: the server refused the money order. The sort leaves the address and
  // the book is asked again without it, rather than an error card over rows
  // that were never refused.
  // Only while the ADDRESS carries a money order: that is the one thing the
  // fallback can take away. The same refusal under any other order is not a
  // refused sort, so it keeps its error card and the address is left alone
  // (rewriting it would only drop the reader's own sort and ask again).
  const valueOrderRefused =
    search.order !== undefined &&
    isValueOrderToken(search.order) &&
    isValueOrderRefusal(page.error);

  // D-301 round 7: URL validation dropped a money order for a reader the
  // mirror KNOWS is not sent totals, so the list never asked for it. The
  // token still sits in the address. The effect below takes it out, as it
  // does after the server's refusal, so both readers end on one address.
  const valueOrderDropped = addressCarriesRefusedValueOrder(rawSearch, valueSort);

  // Both arms hand the drop to the saved-views controller, which removes
  // `order` alone and keeps `view` and every filter, replacing the history
  // entry (D-305 ruling 7). Keyed on the two arms alone: the controller's
  // functions are new on every render, and the arm is what decides.
  const dropRef = useRef(views.dropRefusedOrder);
  dropRef.current = views.dropRefusedOrder;
  const refusedToken = search.order;
  const droppedToken = typeof rawSearch["order"] === "string" ? rawSearch["order"] : undefined;
  useEffect(() => {
    if (valueOrderRefused) dropRef.current(refusedToken);
    else if (valueOrderDropped) dropRef.current(droppedToken);
  }, [valueOrderRefused, valueOrderDropped]);

  /** Typing a NEW customer search abandons the filter in force: leaving the
   * old customer armed while the box says something else is the filter lying
   * about itself. `pickerWrote` is the customer the picker itself last put in
   * the address, so the box empties only when something else moves it (a
   * view applied, All, Reset, the 360's link). */
  const pickerWrote = useRef(customerId);
  useEffect(() => {
    if (customerId === pickerWrote.current) return;
    pickerWrote.current = customerId;
    setCustomerText("");
  }, [customerId]);
  const writeCustomer = (next: string | undefined) => {
    pickerWrote.current = next;
    views.write({ ...search, customer: next });
  };

  /** Widen the book back to every customer: the search box is emptied and the
   * param leaves the ADDRESS, which is the filter's one home. */
  const clearCustomerFilter = () => {
    setCustomerText("");
    writeCustomer(undefined);
  };

  const setOrder = (next: string) => {
    if (!isQuotesOrderToken(next)) return;
    views.write({ ...search, order: next === QUOTES_DEFAULT_ORDER ? undefined : next });
  };

  // The export walks the SAME read with the SAME filters, plus the EFFECTIVE
  // sort token, so the file's order is the one its provenance row names. The
  // customer filter's name comes from the chip's read or a loaded row of that
  // customer, and the button waits until one answers (the order list's rule:
  // line 2 never prints the uuid where the chip prints a name).
  const exportCustomerName =
    filteredCustomerName ??
    rows.find((row) => row.customer_id === customerId)?.customer_display_name;
  const exporter = useSalesExport<Quote>({
    list: "quotes",
    resetKey: queryKey,
    fetchPage: (next, limit, signal) => fetchQuotesPage({ ...query, order, limit }, next, signal),
    serialize: quotesCsv,
    filters: quotesFilters(t, {
      status: search.status ?? "",
      origin: search.origin ?? "",
      owner:
        search.owner !== undefined ? (owners.byId.get(search.owner) ?? search.owner) : undefined,
      customer: customerId !== undefined ? (exportCustomerName ?? customerId) : undefined,
      createdFrom: search.created_from ?? "",
      createdTo: search.created_to ?? "",
      offListOnly: search.off_list === true,
    }),
    order,
    namesPending: customerId !== undefined && exportCustomerName === undefined,
  });

  /** Fan out one verb over the SELECTED, LOADED rows, sequentially, counting
   * both halves. Sequential rather than parallel on purpose: ten simultaneous
   * reminders are ten module-17 dispatches racing one rate limiter, and the
   * refusals that produces are ours, not the server's. */
  const runBulk = async (name: BulkVerb, verb: (id: string) => Promise<unknown>) => {
    setBulkBusy(true);
    setBulkVerb(name);
    setBulkError(undefined);
    setBulkOutcome(undefined);
    let done = 0;
    let failed = 0;
    let lastError: unknown;
    for (const id of selected) {
      try {
        await verb(id);
        done += 1;
      } catch (error) {
        failed += 1;
        lastError = error;
      }
    }
    setBulkBusy(false);
    setBulkOutcome({ done, failed });
    if (failed > 0) setBulkError(lastError);
    setSelected(new Set());
  };

  const selectableRow = (row: Quote) => row.status === "sent";

  // The region is revealed once the views read and the first page have both
  // settled (design-system §5, D-305 ruling 21). A refused reader has no list
  // to wait for, and a paused read is offline, which the region must show
  // rather than wait on forever.
  const paused = page.fetchStatus === "paused";
  const listSettled = page.data !== undefined || page.error !== null;
  const ready = refused || (views.settled && (listSettled || paused));

  const chipLabel = (filter: string, value: string): string =>
    t("sales.common.filterChip", { filter, value });

  return (
    <section className="sales-quotes-screen" data-testid="sales-quotes-screen">
      <PageHeader
        title={t("sales.quotes.title")}
        actions={
          <>
            <div className="sales-toolbar">
              {/* Offered once the first page has rows; until then the same control
              holds its place, inert and invisible, so the head never moves when
              the rows arrive (W2, PR-E4 wave 5). */}
              {refused ? null : rows.length > 0 ? (
                <SalesExportButton exporter={exporter} />
              ) : (
                <SalesExportSlot />
              )}
              {perms.has("sales.quote.write") ? (
                <Button
                  variant="primary"
                  data-testid="sales-quote-new"
                  onClick={() => {
                    void navigate({ to: "/sales/quotes/new" });
                  }}
                >
                  {t("sales.quotes.new")}
                </Button>
              ) : null}
            </div>
          </>
        }
      />

      <SalesExportNotes exporter={exporter} />

      <SalesFilterBar
        data-testid="sales-quotes-filters"
        onClearAll={() => {
          views.write({
            ...search,
            status: undefined,
            origin: undefined,
            owner: undefined,
            created_from: undefined,
            created_to: undefined,
            off_list: undefined,
          });
        }}
        filters={[
          {
            key: "status",
            inline: true,
            applied:
              search.status === undefined
                ? undefined
                : chipLabel(
                    t("sales.quotes.filterStatus"),
                    t(`sales.quoteStatus.${search.status}`),
                  ),
            onClear: () => {
              views.write({ ...search, status: undefined });
            },
            control: (
              <Field label={t("sales.quotes.filterStatus")}>
                <Select
                  value={search.status ?? ""}
                  data-testid="quotes-status"
                  onChange={(e) => {
                    views.write({
                      ...search,
                      status: STATUSES.find((status) => status === e.target.value),
                    });
                  }}
                >
                  <option value="">{t("sales.quotes.filterAll")}</option>
                  {STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {t(`sales.quoteStatus.${value}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            ),
          },
          {
            key: "origin",
            applied:
              search.origin === undefined
                ? undefined
                : chipLabel(
                    t("sales.quotes.filterOrigin"),
                    t(`sales.quotes.origin.${search.origin}`),
                  ),
            onClear: () => {
              views.write({ ...search, origin: undefined });
            },
            control: (
              <Field label={t("sales.quotes.filterOrigin")}>
                <Select
                  value={search.origin ?? ""}
                  data-testid="quotes-origin"
                  onChange={(e) => {
                    views.write({
                      ...search,
                      origin: ORIGINS.find((origin) => origin === e.target.value),
                    });
                  }}
                >
                  <option value="">{t("sales.quotes.filterAll")}</option>
                  {ORIGINS.map((value) => (
                    <option key={value} value={value}>
                      {t(`sales.quotes.origin.${value}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            ),
          },
          {
            key: "owner",
            applied:
              search.owner === undefined
                ? undefined
                : chipLabel(
                    t("sales.quotes.filterOwner"),
                    owners.byId.get(search.owner) ?? t("sales.common.notListed"),
                  ),
            onClear: () => {
              views.write({ ...search, owner: undefined });
            },
            control: (
              <Field label={t("sales.quotes.filterOwner")}>
                <Select
                  value={search.owner ?? ""}
                  data-testid="quotes-owner"
                  onChange={(e) => {
                    views.write({
                      ...search,
                      owner: e.target.value.length > 0 ? e.target.value : undefined,
                    });
                  }}
                >
                  <option value="">{t("sales.quotes.filterAll")}</option>
                  {/* An owner the address names that the index does not hold still
                gets an option, so the control shows the filter in force. */}
                  {search.owner !== undefined && !owners.byId.has(search.owner) ? (
                    <option value={search.owner}>
                      {ownersResolving ? t("ref.resolving") : t("sales.common.notListed")}
                    </option>
                  ) : null}
                  {[...owners.byId.entries()].map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </Select>
              </Field>
            ),
          },
          {
            key: "customer",
            inline: true,
            control: (
              <>
                <Field label={t("sales.quotes.filterCustomer")}>
                  <input
                    value={customerText}
                    placeholder={t("sales.quotes.filterCustomerHint")}
                    data-testid="quotes-customer-q"
                    onChange={(e) => {
                      setCustomerText(e.target.value);
                      if (customerId !== undefined) writeCustomer(undefined);
                    }}
                  />
                </Field>
                {customerQ.length > 1 ? (
                  <Field label={t("sales.quotes.filterCustomer")}>
                    <Select
                      value={customerId ?? ""}
                      data-testid="quotes-customer"
                      onChange={(e) => {
                        writeCustomer(e.target.value.length > 0 ? e.target.value : undefined);
                      }}
                    >
                      <option value="">{t("sales.quotes.filterAll")}</option>
                      {(customerMatches.data?.items ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.display_name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : null}
              </>
            ),
          },
          {
            key: "from",
            applied:
              search.created_from === undefined
                ? undefined
                : chipLabel(t("sales.quotes.filterFrom"), search.created_from),
            onClear: () => {
              views.write({ ...search, created_from: undefined });
            },
            control: (
              <Field label={t("sales.quotes.filterFrom")}>
                <input
                  type="date"
                  value={search.created_from ?? ""}
                  data-testid="quotes-from"
                  onChange={(e) => {
                    views.write({
                      ...search,
                      created_from: e.target.value.length > 0 ? e.target.value : undefined,
                    });
                  }}
                />
              </Field>
            ),
          },
          {
            key: "to",
            applied:
              search.created_to === undefined
                ? undefined
                : chipLabel(t("sales.quotes.filterTo"), search.created_to),
            onClear: () => {
              views.write({ ...search, created_to: undefined });
            },
            control: (
              <Field label={t("sales.quotes.filterTo")}>
                <input
                  type="date"
                  value={search.created_to ?? ""}
                  data-testid="quotes-to"
                  onChange={(e) => {
                    views.write({
                      ...search,
                      created_to: e.target.value.length > 0 ? e.target.value : undefined,
                    });
                  }}
                />
              </Field>
            ),
          },
          {
            key: "off_list",
            applied: search.off_list === true ? t("sales.quotes.filterOffList") : undefined,
            onClear: () => {
              views.write({ ...search, off_list: undefined });
            },
            control: (
              <Field label={t("sales.quotes.filterOffList")}>
                <input
                  type="checkbox"
                  checked={search.off_list === true}
                  data-testid="quotes-off-list"
                  onChange={(e) => {
                    views.write({ ...search, off_list: e.target.checked ? true : undefined });
                  }}
                />
              </Field>
            ),
          },
        ]}
      />

      <ViewsRegion ready={ready} cols={columns.length}>
        {!refused ? <ViewSwitcher views={views} /> : null}

        {customerId !== undefined ? (
          // The filter said out loud. `sales-toolbar` is the register's flex row
          // (no new class for a one-screen chip), `sales-hint` its muted ink.
          <div
            className="sales-toolbar"
            data-testid="sales-quotes-customer-chip"
            data-customer={customerId}
          >
            <span className="sales-hint">{t("sales.quotes.filterCustomer")}</span>
            {/* `RefLabel` rather than a name interpolated into a sentence: the
                read has four outcomes and three of them are not a name — still
                in flight, refused (a sales user who is not assigned this
                customer), or answered with nothing. The register's primitive
                says which without inventing one, and a uuid printed where a
                name goes reads as corrupt data. */}
            <span data-testid="sales-quotes-customer-name">
              <RefLabel
                value={filteredCustomerName}
                id={customerId}
                pending={filteredCustomer.isPending}
              />
            </span>
            <Button
              size="sm"
              data-testid="sales-quotes-customer-clear"
              onClick={clearCustomerFilter}
            >
              {t("sales.common.filterCustomerClear")}
            </Button>
          </div>
        ) : null}

        {canSend ? (
          <p className="sales-hint" data-testid="sales-quotes-bulk-note">
            {t("sales.quotes.bulkSentOnly")}
          </p>
        ) : null}

        {bulkOutcome !== undefined ? (
          <p className="sales-banner" role="status" data-testid="sales-quotes-bulk-result">
            {t("sales.quotes.bulkResult", { done: bulkOutcome.done, failed: bulkOutcome.failed })}
          </p>
        ) : null}
        {bulkError !== undefined ? (
          <MutationFailureNote
            data-testid="sales-mutation-failed"
            text={t(
              bulkVerb === "expire"
                ? "sales.quotes.bulkExpireFailed"
                : "sales.quotes.bulkRemindFailed",
            )}
            error={bulkError}
          />
        ) : null}

        {refused ? (
          <ForbiddenState title={t("sales.access.title")} body={t("sales.access.quotes")} />
        ) : null}

        {!refused && paused && rows.length === 0 ? (
          <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
        ) : null}

        {!refused && page.isPending && !paused && rows.length === 0 ? (
          <TableSkeleton rows={5} cols={columns.length} label={t("sales.quotes.loading")} />
        ) : null}

        {!refused &&
        page.error !== null &&
        !(page.error instanceof UnauthorizedError) &&
        !valueOrderRefused ? (
          <ErrorState
            title={t("sales.quotes.error")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(page.error)}
            onRetry={() => {
              void page.refetch();
            }}
          />
        ) : null}

        {!refused && !page.isPending && !paused && page.error === null && rows.length === 0 ? (
          // Two empties, because they ask for different acts: an empty BOOK is
          // "write your first quote"; an empty FILTER is "widen it".
          isFiltered ? (
            <EmptyState
              title={t("sales.quotes.emptyFilteredTitle")}
              body={t("sales.quotes.emptyFilteredBody")}
            />
          ) : (
            <EmptyState title={t("sales.quotes.emptyTitle")} body={t("sales.quotes.emptyBody")} />
          )
        ) : null}

        {rows.length > 0 ? (
          <>
            <DataTable
              pager={bookPager(t, i18n.language, pageView, {
                turning,
                onNext: () => {
                  const next = shownData?.next_cursor;
                  if (next === null || next === undefined) return;
                  pages.next(next, rows.length);
                  setSelected(new Set());
                },
                onPrev: () => {
                  pages.prev();
                  setSelected(new Set());
                },
              })}
              data-testid="sales-quotes-table"
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              empty={null}
              caption={t("sales.quotes.tableCaption")}
              rowTestId="sales-quote-row"
              rowProps={(row) => ({
                "data-status": row.status,
                "data-origin": row.origin,
                "data-off-list": row.has_off_list_line ? "true" : "false",
              })}
              sticky
              keyboardNav
              columnConfig={views.columnConfig}
              sort={{
                value: order,
                onChange: setOrder,
                toggleLabel: (header: string, next: "ascending" | "descending") =>
                  next === "descending"
                    ? t("sales.quotes.sortDesc", { column: header })
                    : t("sales.quotes.sortAsc", { column: header }),
              }}
              {...(canSend
                ? {
                    selectable: {
                      selected,
                      onChange: setSelected,
                      headerLabel: t("sales.quotes.selectAll"),
                      rowLabel: (row: Quote) =>
                        t("sales.quotes.selectRow", { quote: row.quote_no }),
                      isRowSelectable: selectableRow,
                      testId: "sales-quote-select",
                    },
                    // Spread, not a `| undefined` value: `exactOptionalPropertyTypes`
                    // distinguishes "absent" from "present and undefined", and the
                    // primitive's contract is that the bar is ABSENT when nothing
                    // is selected.
                    ...(selected.size > 0
                      ? {
                          bulkBar: {
                            countLabel: t("sales.quotes.bulkSelected", { count: selected.size }),
                            clearLabel: t("sales.quotes.bulkClear"),
                            onClear: () => {
                              setSelected(new Set());
                            },
                            children: (
                              <>
                                <Button
                                  size="sm"
                                  data-testid="sales-quotes-remind"
                                  busy={bulkBusy && remind.isPending}
                                  busyLabel={t("sales.quotes.bulkWorking")}
                                  onClick={() => {
                                    void runBulk("remind", (id) => remind.mutateAsync(id));
                                  }}
                                >
                                  {t("sales.quotes.bulkRemind")}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  data-testid="sales-quotes-expire"
                                  busy={bulkBusy && expire.isPending}
                                  busyLabel={t("sales.quotes.bulkWorking")}
                                  onClick={() => {
                                    void runBulk("expire", (id) => expire.mutateAsync(id));
                                  }}
                                >
                                  {t("sales.quotes.bulkExpire")}
                                </Button>
                              </>
                            ),
                          },
                        }
                      : {}),
                  }
                : {})}
              peek={{
                key: (row) => peekCode("quote", row.id),
                current: peek.code,
                onOpen: (code) => {
                  peek.open(code);
                },
                onClose: peek.close,
                label: t("sales.peek.open"),
                rowLabel: (row) => t("sales.peek.openNamed", { name: row.quote_no }),
              }}
              onRowClick={(row) => {
                void navigate({
                  to: "/sales/quotes/$quoteId",
                  params: { quoteId: row.id },
                });
              }}
            />
          </>
        ) : null}
      </ViewsRegion>

      <SalesPeek peek={peek} />
    </section>
  );
}
